import React, { Fragment } from 'react';

/**
 * Glacier code highlighter — pure-JS, lazy-loaded grammars.
 *
 * Token vocabulary is intentionally tiny — five classes that map directly to
 * the design system's `--ctok-*` palette (see `design/styles.css`):
 *
 *   k → keyword     (`.ctok-k`)
 *   s → string      (`.ctok-s`)
 *   c → comment     (`.ctok-c`)
 *   f → function    (`.ctok-f`)
 *   t → type        (`.ctok-t`)
 *   n → number      (`.ctok-n`)
 *
 * Anything that doesn't fit those classes falls through as plain text. This
 * keeps each per-language grammar to ~10–30 regex rules and the entire
 * highlighter under a few KB gzipped — Prism-lite by design.
 *
 * Output is React nodes only (one fragment per source line), so callers can
 * render line numbers without parsing HTML. Never returns DOM strings — no
 * `dangerouslySetInnerHTML`, no XSS surface.
 */

// Module-level grammar cache. Once a grammar has been dynamically imported,
// subsequent highlight() calls for the same language are synchronous-ish
// (still wrapped in a Promise, but the import() resolves immediately because
// the module is already in the module map).
const GRAMMAR_CACHE = new Map();

// Negative cache: languages we've already determined have no grammar. Avoids
// repeated network/module-resolution failures for unknown langs.
const UNKNOWN_LANGS = new Set();

/**
 * Synchronous fallback used while async grammar is loading, or for callers
 * (e.g. `useHighlight`) that need an initial value before the promise settles.
 * Splits on `\n`, preserves blank lines, and wraps each line in a single
 * `<span>` with no token classes.
 */
export function highlightPlain(text) {
  const lines = String(text ?? '').split('\n');
  return lines.map((line, i) => (
    <Fragment key={i}>
      <span>{line}</span>
    </Fragment>
  ));
}

/**
 * Asynchronously highlight `text` with the grammar for `lang`. Returns an
 * array of React fragments, one per line — each fragment contains zero or
 * more `<span class="ctok-X">` token spans plus interleaved plain-text spans
 * for anything that didn't match a rule.
 *
 * Unknown languages return plain output (same shape as `highlightPlain`)
 * without throwing — the highlighter is best-effort.
 */
export async function highlight(text, lang) {
  if (text == null) return [];
  const key = String(lang ?? '').toLowerCase();
  if (!key || UNKNOWN_LANGS.has(key)) return highlightPlain(text);

  let grammar = GRAMMAR_CACHE.get(key);
  if (!grammar) {
    try {
      // Dynamic import — Vite/Rollup will code-split each grammar into its
      // own chunk, so unrequested languages are never shipped to the client.
      const mod = await import(`./grammars/${key}.js`);
      grammar = mod.default;
      if (!Array.isArray(grammar)) throw new Error('grammar default export must be an array');
      GRAMMAR_CACHE.set(key, grammar);
    } catch {
      UNKNOWN_LANGS.add(key);
      return highlightPlain(text);
    }
  }

  return tokenizeLines(String(text), grammar);
}

/**
 * Test-only escape hatch — lets unit tests reset the caches between cases so
 * a negative-cache hit from a previous test doesn't poison the next one.
 * Not part of the public surface; don't import this from app code.
 */
export function __resetCaches() {
  GRAMMAR_CACHE.clear();
  UNKNOWN_LANGS.clear();
}

// --- internals ---------------------------------------------------------

function tokenizeLines(text, grammar) {
  const lines = text.split('\n');
  return lines.map((line, i) => (
    <Fragment key={i}>{tokenizeLine(line, grammar)}</Fragment>
  ));
}

function tokenizeLine(line, grammar) {
  if (line.length === 0) return null;

  const out = [];
  let i = 0;
  let plainStart = 0;
  let nodeKey = 0;

  const flushPlain = (until) => {
    if (plainStart < until) {
      out.push(<span key={`p${nodeKey++}`}>{line.slice(plainStart, until)}</span>);
    }
  };

  while (i < line.length) {
    let matched = null;
    let matchLen = 0;
    let matchType = null;

    for (const rule of grammar) {
      const re = rule.pattern;
      // Sticky regexes only — set lastIndex and run exec at the cursor.
      re.lastIndex = i;
      const m = re.exec(line);
      if (m && m.index === i && m[0].length > 0) {
        matched = m[0];
        matchLen = m[0].length;
        matchType = rule.type;
        break;
      }
    }

    if (matched) {
      flushPlain(i);
      out.push(
        <span key={`t${nodeKey++}`} className={`ctok-${matchType}`}>
          {matched}
        </span>,
      );
      i += matchLen;
      plainStart = i;
    } else {
      i += 1;
    }
  }

  flushPlain(i);
  return out;
}
